import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Image from '../AppImage';

const CartSidebar = ({ isOpen = false, onClose, cartItems = [], onUpdateQuantity, onRemoveItem, onCheckout }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Sample cart items for demonstration
  // No starter mock — sidebar is empty until the user adds something.
  const defaultCartItems = [];

  const items = cartItems?.length > 0 ? cartItems : defaultCartItems;

  const subtotal = items?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const shipping = subtotal > 50 ? 0 : 9.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    if (onUpdateQuantity) {
      onUpdateQuantity(itemId, newQuantity);
    }
  };

  const handleRemoveItem = (itemId) => {
    if (onRemoveItem) {
      onRemoveItem(itemId);
    }
  };

  const handleCheckout = async () => {
    setIsLoading(true);
    
    try {
      // Simulate checkout process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (onCheckout) {
        onCheckout(items);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />
      {/* Sidebar */}
      <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-background shadow-elevation-3 transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="ShoppingCart" size={24} className="text-primary" />
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Shopping Cart
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {items?.length === 0 ? (
            /* Empty Cart */
            (<div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="ShoppingCart" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-heading font-medium text-foreground mb-2">
                Your cart is empty
              </h3>
              <p className="text-muted-foreground mb-6">
                Discover our beautiful collection of beauty products
              </p>
              <Button onClick={onClose}>
                Continue Shopping
              </Button>
            </div>)
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {items?.map((item) => (
                  <div key={item?.id} className="flex space-x-4 p-4 bg-card rounded-lg border border-border">
                    <div className="flex-shrink-0">
                      <Image
                        src={item?.image}
                        alt={item?.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground truncate">
                            {item?.name}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            {item?.brand} • {item?.variant}
                          </p>
                          {!item?.inStock && (
                            <p className="text-xs text-error mt-1">Out of stock</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(item?.id)}
                          className="flex-shrink-0 ml-2"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleQuantityChange(item?.id, item?.quantity - 1)}
                            disabled={item?.quantity <= 1}
                            className="w-8 h-8"
                          >
                            <Icon name="Minus" size={12} />
                          </Button>
                          <span className="text-sm font-medium text-foreground min-w-[2rem] text-center">
                            {item?.quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleQuantityChange(item?.id, item?.quantity + 1)}
                            disabled={!item?.inStock}
                            className="w-8 h-8"
                          >
                            <Icon name="Plus" size={12} />
                          </Button>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-sm font-medium text-foreground">
                            ${(item?.price * item?.quantity)?.toFixed(2)}
                          </div>
                          {item?.originalPrice && (
                            <div className="text-xs text-muted-foreground line-through">
                              ${(item?.originalPrice * item?.quantity)?.toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="border-t border-border p-6 space-y-4">
                {/* Shipping Notice */}
                {shipping > 0 && (
                  <div className="p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <p className="text-sm text-accent-foreground">
                      <Icon name="Truck" size={16} className="inline mr-2" />
                      Add ${(50 - subtotal)?.toFixed(2)} more for free shipping
                    </p>
                  </div>
                )}

                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">${subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-foreground">
                      {shipping === 0 ? 'Free' : `$${shipping?.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="text-foreground">${tax?.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-border pt-2">
                    <div className="flex justify-between text-base font-semibold">
                      <span className="text-foreground">Total</span>
                      <span className="text-foreground">${total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Checkout Button */}
                <Button
                  fullWidth
                  loading={isLoading}
                  onClick={handleCheckout}
                  disabled={items?.some(item => !item?.inStock)}
                  iconName="CreditCard"
                  iconPosition="left"
                >
                  Proceed to Checkout
                </Button>

                {/* Continue Shopping */}
                <Button
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                >
                  Continue Shopping
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;